import { UploadStatus } from "@/shared/enums/upload-status";
import { RootState } from "@/store";
import { artifactUploaderActions } from "@/store/artifactUploader/artifactUploaderAction";
import { UploadProgressItem } from "@/store/artifactUploader/artifactUploaderSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Estados que indican que un upload ha finalizado (éxito o error)
 */
const FINISHED_UPLOAD_STATUSES: UploadStatus[] = [
	UploadStatus.COMPLETED,
	UploadStatus.UPLOADING_ERROR,
	UploadStatus.ANALYZING_ERROR,
];

/**
 * Estados que indican que un upload está activo/en progreso
 */
const ACTIVE_UPLOAD_STATUSES: UploadStatus[] = [
	UploadStatus.UPLOADING,
	UploadStatus.ANALYZING,
	UploadStatus.PENDING,
];

/**
 * Tiempo de espera antes de limpiar uploads finalizados en rutas excluidas
 */
const CLEANUP_DELAY_MS = 2000;

interface UseUploadCleanupProps {
	isInExcludedRoute: boolean;
}

/**
 * Hook para manejar la limpieza automática de uploads finalizados
 */
export const useUploadCleanup = ({
	isInExcludedRoute,
}: UseUploadCleanupProps) => {
	const dispatch = useDispatch();
	const { activeUploads } = useSelector((state: RootState) => state.uploadFile);

	// Limpiar uploads en rutas excluidas
	useEffect(() => {
		if (!isInExcludedRoute) return;

		const finishedUploads = activeUploads.filter(
			(upload) =>
				FINISHED_UPLOAD_STATUSES.includes(upload.status) &&
				!upload.hideFromToast,
		);

		// Ocultar del toast
		for (const upload of finishedUploads) {
			dispatch(
				artifactUploaderActions.hideUploadFromToast({
					id: upload.id,
					datasetId: upload.datasetId,
				}),
			);
		}

		// Limpiar si todos están finalizados
		const allFinished = activeUploads.every(
			(upload) =>
				FINISHED_UPLOAD_STATUSES.includes(upload.status) ||
				upload.hideFromToast,
		);

		if (allFinished && activeUploads.length > 0) {
			const timer = setTimeout(() => {
				dispatch(artifactUploaderActions.clearAllFinishedUploads());
			}, CLEANUP_DELAY_MS);

			return () => clearTimeout(timer);
		}
	}, [activeUploads, isInExcludedRoute, dispatch]);

	useEffect(() => {
		if (isInExcludedRoute) return;

		const hasActiveUploads = activeUploads.some((upload) =>
			ACTIVE_UPLOAD_STATUSES.includes(upload.status),
		);

		const hasCompletedUploads = activeUploads.some(
			(upload) => upload.status === UploadStatus.COMPLETED,
		);

		// Si no hay archivos activos pero sí completados, limpiarlos inmediatamente
		if (!hasActiveUploads && hasCompletedUploads) {
			dispatch(artifactUploaderActions.clearCompletedUploads());
		}
	}, [activeUploads, isInExcludedRoute, dispatch]);
};

/**
 * Helper para filtrar uploads que deben mostrarse
 */
export const filterVisibleUploads = (
	uploads: UploadProgressItem[],
): UploadProgressItem[] => {
	return uploads.filter((upload) => {
		if (upload.hideFromToast) return false;

		// Mostrar SOLO archivos activos (en proceso) y errores
		// NO mostrar archivos COMPLETED
		return (
			upload.status === UploadStatus.UPLOADING ||
			upload.status === UploadStatus.ANALYZING ||
			upload.status === UploadStatus.PENDING ||
			upload.status === UploadStatus.UPLOADING_ERROR ||
			upload.status === UploadStatus.ANALYZING_ERROR
		);
	});
};
